import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

/**
 * Scroll-triggered animation wrapper using IntersectionObserver + framer-motion.
 *
 * Props:
 *   variant: "fadeUp" | "fadeDown" | "fadeLeft" | "fadeRight" | "scaleUp" | "blur"
 *   delay: number (seconds)
 *   duration: number (seconds)
 *   threshold: 0-1
 *   once: boolean (animate only once)
 *   stagger: number (seconds, for staggering children)
 */

const variants = {
  fadeUp: {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 },
  },
  fadeDown: {
    hidden: { opacity: 0, y: -30 },
    visible: { opacity: 1, y: 0 },
  },
  fadeLeft: {
    hidden: { opacity: 0, x: -60 },
    visible: { opacity: 1, x: 0 },
  },
  fadeRight: {
    hidden: { opacity: 0, x: 60 },
    visible: { opacity: 1, x: 0 },
  },
  scaleUp: {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1 },
  },
  blur: {
    hidden: { opacity: 0, filter: "blur(10px)" },
    visible: { opacity: 1, filter: "blur(0px)" },
  },
};

export const AnimateIn = ({
  children,
  variant = "fadeUp",
  delay = 0,
  duration = 0.7,
  threshold = 0.15,
  once = true,
  className = "",
  style = {},
}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold, once]);

  const v = variants[variant] || variants.fadeUp;

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isVisible ? "visible" : "hidden"}
      variants={v}
      transition={{
        duration,
        delay,
        ease: [0.25, 0.1, 0.25, 1],
      }}
      className={className}
      style={style}
    >
      {children}
    </motion.div>
  );
};

/**
 * Staggered children wrapper — each child animates in sequence.
 */
export const StaggerIn = ({
  children,
  variant = "fadeUp",
  stagger = 0.12,
  duration = 0.6,
  threshold = 0.1,
  once = true,
  className = "",
}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) observer.disconnect();
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold, once]);

  const v = variants[variant] || variants.fadeUp;

  return (
    <div ref={ref} className={className}>
      {React.Children.map(children, (child, i) => (
        <motion.div
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
          variants={v}
          transition={{
            duration,
            delay: i * stagger,
            ease: [0.25, 0.1, 0.25, 1],
          }}
          style={{ height: '100%' }}
        >
          {child}
        </motion.div>
      ))}
    </div>
  );
};
